import type { ReactElement } from 'react';

import { DateTime } from 'luxon';

import type { Icon } from '@phosphor-icons/react';
import {
  CarIcon, CheckCircle, DesktopIcon, XCircle, Question,
} from '@phosphor-icons/react';

import {
  AbsenceType, AbsenceTypeInput, BonusType, ContractType,
} from '../schema/admin';
import type { AbsenceType as AdminAbsenceType } from '../schema/admin';
import type { AbsenceTypeInput as UserAbsenceType, ReportedLeaveDays } from '../schema/user';
import {
  CalendarDayType, EmploymentContractType, ProgressionLevel, ResourceKind, Settlement,
} from '../schema/user';
import { CellState, ExtendedAbsenceTypeInput, type ExtendedAbsenceTypeInputType } from '../services/additionalTypes';
import { compareTimeUnits, getDateTime } from '../services/date';
import { TimeUnit } from '../services/format';
import type { TranslatingFn } from '../services/i18n';
import type { Nullable } from '../services/object';
import palette from '../theme/palette';

export const progressionInapplicableEmploymentTypes: readonly EmploymentContractType[] = [
  EmploymentContractType.UOD,
];

export const invoiceDates = {
  lockDate: (date: Date | DateTime): Date => getDateTime(date)
    .plus({ month: 1 })
    .set({ day: 10 })
    .endOf('day')
    .toJSDate(),
  unlockDate: (date: Date | DateTime): Date => getDateTime(date)
    .startOf('month')
    .toJSDate(),
};

export const onCallSettlement = Settlement.DAILY;

export const settlementToTimeUnit = {
  [Settlement.HOURLY]: TimeUnit.hour,
  [Settlement.DAILY]: TimeUnit.day,
  [Settlement.MONTHLY]: TimeUnit.month,
};

export const monthsFromLastRateChangeLevels = {
  light: 4,
  medium: 7,
  strong: 10,
};

export const progressionLevelOrder = [
  ProgressionLevel.ENGINEERINTERN,
  ProgressionLevel.ENGINEERJUNIOR,
  ProgressionLevel.ENGINEERMID,
  ProgressionLevel.ENGINEERSENIOR,
  ProgressionLevel.ENGINEERSTAFF,
  ProgressionLevel.ENGINEERPRINCIPAL,
  ProgressionLevel.NONENGINEERINTERN,
  ProgressionLevel.NONENGINEERJUNIOR,
  ProgressionLevel.NONENGINEERREGULAR,
  ProgressionLevel.NONENGINEERSENIOR,
  ProgressionLevel.NONENGINEEREXPERT,
  ProgressionLevel.ENGINEERJ1,
  ProgressionLevel.ENGINEERJ2,
  ProgressionLevel.ENGINEERJ3,
  ProgressionLevel.ENGINEERM1,
  ProgressionLevel.ENGINEERM2,
  ProgressionLevel.ENGINEERM3,
  ProgressionLevel.ENGINEERM4,
  ProgressionLevel.ENGINEERS1,
  ProgressionLevel.ENGINEERS2,
  ProgressionLevel.ENGINEERS3,
  ProgressionLevel.ENGINEERS4,
  ProgressionLevel.ENGINEERS5,
  ProgressionLevel.ENGINEERE1,
  ProgressionLevel.ENGINEERE2,
  ProgressionLevel.NONENGINEERJ1,
  ProgressionLevel.NONENGINEERJ2,
  ProgressionLevel.NONENGINEERJ3,
  ProgressionLevel.NONENGINEERM1,
  ProgressionLevel.NONENGINEERM2,
  ProgressionLevel.NONENGINEERM3,
  ProgressionLevel.NONENGINEERM4,
  ProgressionLevel.NONENGINEERS1,
  ProgressionLevel.NONENGINEERS2,
  ProgressionLevel.NONENGINEERS3,
  ProgressionLevel.NONENGINEERS4,
  ProgressionLevel.NONENGINEERE1,
  ProgressionLevel.NONENGINEERE2,
];

enum ProgressionType {
  Engineer = 'Engineer',
  NonEngineer = 'NonEngineer',
}

export const isSmlLevel = (level: ProgressionLevel): boolean => /\d$/.test(level);
export const isNonEngineerLevel = (level: ProgressionLevel): boolean => level.includes(ProgressionType.NonEngineer);

export const overtimeEmoji = '⏱';
export const onCallEmojis = {
  onCall: '📞',
  day: '☀️',
  night: '🦉',
};

export const incomeEmoji = {
  base: '💰',
  overtime: '⏱',
  onCall: '📞',
  bonuses: '🍭',
  businessTravel: '🌍',
  refundedBenefits: '🎁',
  notRefundedBenefits: '👎',
  transferAmount: '💰',
  taxPrepayment: '💵',
  zusContribution: '🏥',
  ppkAmount: '🗂️',
  transferDeduction: '🗃',
  totalAccountingData: '💸',
  remoteWork: '🏠',
  referralBonus: '👬 ',
  appreciationBonus: '🥇',
  languageBonus: '🇬🇧',
  totalBonuses: '💸',
  otherBonus: '💵 ',
  trainingPackageBonus: '🦉',
  remoteWorkEquipmentBonus: '🖥️',
};

export const incomeBenefitColors = {
  refunded: palette.support.success[20],
  notRefunded: palette.support.alert[30],
};

export const absenceColors: Record<AdminAbsenceType | ExtendedAbsenceTypeInputType | UserAbsenceType, string> = {
  AnnualLeaveHourly: palette.support.info[30],
  AnnualLeave: palette.support.info[30],
  SickLeave: palette.support.alert[30],
  SickFamilyMemberCareLeave: palette.support.alert[30],
  SpecialLeave: palette.support.success[30],
  UnpaidLeave: palette.support.warning[30],
  FlextimeLeave: palette.additional.teal[20],
  MaternityLeave: palette.additional.magenta[30],
  PaternityLeave: palette.additional.magenta[30],
  OutOfOffice: palette.support.warning[30],
  DueToForceMajeureLeave: palette.greyscale[60],
  ChildCareLeave: palette.additional.yellow[30],
  ParentalLeave: palette.support.success[30],
  PaidLeave: palette.additional.purple[30],
  CaregivingLeave: palette.additional.purple[30],
};

// Mirrors AbsenceType.adminOnly in nfs-core, which the GraphQL schema does not expose.
export const adminOnlyAbsenceTypes: AbsenceType[] = [
  AbsenceType.MATERNITYLEAVE,
  AbsenceType.PATERNITYLEAVE,
];

export const contractsWithAnnualLeave: ContractType[] = [ContractType.UOP];
export const contractsWithOvertime: ContractType[] = [ContractType.B2B, ContractType.UZ];
export const contractsInTeamCalendars: ContractType[] = Object.values(ContractType)
  .filter((type) => type !== ContractType.UOD);

export const incomeBonusDisplayOrder = [
  BonusType.EMPLOYEEREFERRAL,
  BonusType.APPRECIATION,
  BonusType.LANGUAGELEARNING,
  BonusType.TRAININGPACKAGE,
  BonusType.REMOTEWORKEQUIPMENT,
  BonusType.TRAININGPACKAGEEXTERNALDEDUCTIONVALUE,
  BonusType.BUSINESSTRAVEL,
  BonusType.CLIENTREFERRAL,
  BonusType.CONFERENCE,
  BonusType.COSTRECHARGE,
  BonusType.SALESCOMMISSIONS,
  BonusType.SEVERANCEPAY,
  BonusType.SHAREHOLDERDIVIDEND,
  BonusType.OTHER,
];

export const checkVersionIntervalMS = {
  focused: 30000,
  blurred: 180000,
};

export const maxReportingRange = {
  from: DateTime.local().minus({ years: 10 }).toJSDate(),
  to: DateTime.local().plus({ years: 10 }).toJSDate(),
};

export const emptyReportingRange = {
  from: maxReportingRange.from,
  to: maxReportingRange.from,
};

export enum ApprovalStatus {
  toBeChecked = 'toBeChecked',
  correct = 'correct',
  incorrect = 'incorrect',
}

export const approvalStatusIcon: Record<ApprovalStatus, ReactElement> = {
  [ApprovalStatus.correct]: <CheckCircle color={palette.support.success[60]} />,
  [ApprovalStatus.incorrect]: <XCircle color={palette.support.warning[60]} />,
  [ApprovalStatus.toBeChecked]: <Question color={palette.support.info[60]} />,
};

export type BookedState = CellState.approved | CellState.assigned | CellState.pending;

export const bookedStateColors: Readonly<Record<BookedState, Readonly<{ background: string; color: string }>>> = {
  [CellState.pending]: { background: palette.support.warning[10], color: palette.support.warning[50] },
  [CellState.approved]: { background: palette.primary[10], color: palette.primary[60] },
  [CellState.assigned]: { background: palette.support.info[10], color: palette.support.info[60] },
};

export const shouldWarnAboutMissingEntriesDayOffset = 2;

export const holidayColor = palette.greyscale[50];
export const nonWorkingDayColor = palette.greyscale[50];
export const remoteWorkAccentColor = palette.support.info[30];

export const timeReportModalContainerOverflowChangePadding = 40;

export const defaultHoursInWorkingDay = 8;
export const hoursInAbsenceDay = defaultHoursInWorkingDay;
export const defaultWorkingTime = 1;
export const noProjectId = 'noProjectId';
export const trDatePickerEnabledFutureYearsAmount = 1;

export const baseCurrency = 'PLN'
export const defaultIncomeModalCurrency = baseCurrency;

type LeaveEntryConfig<T> = Partial<Record<AbsenceType, T>>;

export const leaveCouldCoexistWithTypes: LeaveEntryConfig<AbsenceType[]> = {
  [AbsenceType.SICKLEAVE]: [AbsenceType.ANNUALLEAVE],
  [AbsenceType.ANNUALLEAVE]: [AbsenceType.SICKLEAVE],
};

export const leaveCouldCoexistWithOnCalls: LeaveEntryConfig<Partial<Record<ContractType, boolean>>> = {
  [AbsenceTypeInput.SPECIALLEAVE]: {
    [ContractType.UOP]: false,
  },
  [AbsenceTypeInput.MATERNITYLEAVE]: {
    [ContractType.UOP]: false,
  },
  [AbsenceTypeInput.PATERNITYLEAVE]: {
    [ContractType.UOP]: false,
  },
  [AbsenceTypeInput.UNPAIDLEAVE]: {
    [ContractType.UOP]: false,
  },
  [AbsenceTypeInput.ANNUALLEAVE]: {
    [ContractType.UOP]: false,
  },
  [AbsenceTypeInput.SICKLEAVE]: {
    [ContractType.UOP]: false,
  },
  [AbsenceTypeInput.SICKFAMILYMEMBERCARELEAVE]: {
    [ContractType.UOP]: false,
  },
};

export const contractToApplicableHolidayTypes: Record<ContractType, CalendarDayType[]> = {
  [ContractType.B2B]: [CalendarDayType.HOLIDAY],
  [ContractType.MANAGEMENT]: [CalendarDayType.HOLIDAY],
  [ContractType.SUBCONTRACT]: [CalendarDayType.HOLIDAY],
  [ContractType.UOD]: [CalendarDayType.HOLIDAY],
  [ContractType.UZ]: [CalendarDayType.HOLIDAY],
  [ContractType.UOP]: [CalendarDayType.HOLIDAY, CalendarDayType.FREEDAYFOROTHERDAY],
};

export const absenceTypeToInput: Record<AbsenceType, AbsenceTypeInput> = {
  [AbsenceType.ANNUALLEAVE]: AbsenceTypeInput.ANNUALLEAVE,
  [AbsenceType.FLEXTIMELEAVE]: AbsenceTypeInput.FLEXTIMELEAVE,
  [AbsenceType.SICKLEAVE]: AbsenceTypeInput.SICKLEAVE,
  [AbsenceType.SICKFAMILYMEMBERCARELEAVE]: AbsenceTypeInput.SICKFAMILYMEMBERCARELEAVE,
  [AbsenceType.MATERNITYLEAVE]: AbsenceTypeInput.MATERNITYLEAVE,
  [AbsenceType.PATERNITYLEAVE]: AbsenceTypeInput.PATERNITYLEAVE,
  [AbsenceType.SPECIALLEAVE]: AbsenceTypeInput.SPECIALLEAVE,
  [AbsenceType.UNPAIDLEAVE]: AbsenceTypeInput.UNPAIDLEAVE,
  [AbsenceType.PAIDLEAVE]: AbsenceTypeInput.PAIDLEAVE,
  [AbsenceType.OUTOFOFFICE]: AbsenceTypeInput.OUTOFOFFICE,
  [AbsenceType.CHILDCARELEAVE]: AbsenceTypeInput.CHILDCARELEAVE,
  [AbsenceType.PARENTALLEAVE]: AbsenceTypeInput.PARENTALLEAVE,
  [AbsenceType.DUETOFORCEMAJEURELEAVE]: AbsenceTypeInput.DUETOFORCEMAJEURELEAVE,
  [AbsenceType.CAREGIVINGLEAVE]: AbsenceTypeInput.CAREGIVINGLEAVE,
};

export const absenceInputToReportedLeaveDaysKey: Record<Exclude<AbsenceTypeInput, 'FlextimeLeave'>, keyof Omit<ReportedLeaveDays, '__typename'>> = {
  [AbsenceTypeInput.ANNUALLEAVE]: 'annual',
  [AbsenceTypeInput.SICKLEAVE]: 'sickLeave',
  [AbsenceTypeInput.SICKFAMILYMEMBERCARELEAVE]: 'sickFamilyMemberCare',
  [AbsenceTypeInput.MATERNITYLEAVE]: 'maternity',
  [AbsenceTypeInput.PATERNITYLEAVE]: 'paternity',
  [AbsenceTypeInput.SPECIALLEAVE]: 'special',
  [AbsenceTypeInput.UNPAIDLEAVE]: 'unpaid',
  [AbsenceTypeInput.PAIDLEAVE]: 'paid',
  [AbsenceTypeInput.OUTOFOFFICE]: 'outOfOffice',
  [AbsenceTypeInput.CHILDCARELEAVE]: 'childCare',
  [AbsenceTypeInput.PARENTALLEAVE]: 'parental',
  [AbsenceTypeInput.DUETOFORCEMAJEURELEAVE]: 'dueToForceMajeure',
  [AbsenceTypeInput.CAREGIVINGLEAVE]: 'caregiving',
};

export const preferredLeaveOrder: AbsenceTypeInput[] = [
  AbsenceTypeInput.ANNUALLEAVE,
  AbsenceTypeInput.SPECIALLEAVE,
  AbsenceTypeInput.SICKLEAVE,
  AbsenceTypeInput.SICKFAMILYMEMBERCARELEAVE,
  AbsenceTypeInput.UNPAIDLEAVE,
  AbsenceTypeInput.FLEXTIMELEAVE,
  AbsenceTypeInput.MATERNITYLEAVE,
  AbsenceTypeInput.PATERNITYLEAVE,
  AbsenceTypeInput.CAREGIVINGLEAVE,
  AbsenceTypeInput.PARENTALLEAVE,
  AbsenceTypeInput.DUETOFORCEMAJEURELEAVE,
  AbsenceTypeInput.OUTOFOFFICE,
  AbsenceTypeInput.CHILDCARELEAVE,
];

export const hourlyLeaveWithNoWholeDayOption: ExtendedAbsenceTypeInputType[] = [
  ExtendedAbsenceTypeInput.ANNUALLEAVE_HOURLY,
];

export const contractsWithRemoteWork: ContractType[] = [ContractType.UOP];
export const contractsWithWorkingTime: ContractType[] = [ContractType.UOP];

export const workedTimeColor = palette.additional.greenCyan[80];
export const flexTimeColor = palette.support.info[80];

export const fakeTimeBasedChangePrefix = 'fake-change';

export const endOfAnnualLeaveCarryOverPeriod = (): Date => {
  const today = DateTime.local();

  const thisYearsPeriodEnd = today.set({ day: 30, month: 9 });

  if (compareTimeUnits(today, thisYearsPeriodEnd, 'day') < 0) {
    return thisYearsPeriodEnd.toJSDate();
  }

  return thisYearsPeriodEnd.plus({ year: 1 }).toJSDate();
};

export const getLeaveDescriptions = (t: TranslatingFn): Record<ExtendedAbsenceTypeInputType, Nullable<string>> => ({
  AnnualLeave: t('holiday, max. 20 or 26 days, 100% paid'),
  AnnualLeaveHourly: null,
  FlextimeLeave: t('absence to be made up on another day'),
  UnpaidLeave: t('if you need more time off, unpaid'),
  SpecialLeave: t('birth of a child, wedding, funeral etc., 100% paid'),
  MaternityLeave: null,
  PaternityLeave: null,
  SickLeave: t('due to health reasons, 80% paid'),
  SickFamilyMemberCareLeave: t('to care for a sick child under 14 (max. 60 days) or family member (max. 14 days), 80% paid'),
  ChildCareLeave: t('to care for a child under 14, max. 16 hours or 2 days, 100% paid'),
  CaregivingLeave: t('to care for a family or household member, max. 5 days, unpaid'),
  DueToForceMajeureLeave: t('for urgent matters, max. 16 hours or 2 days, 50% paid'),
  OutOfOffice: null,
  ParentalLeave: t('max. 35 months, unpaid'),
  PaidLeave: t('for managers only, 100% paid'),
});

export const trainingPackageTimeLimit = 24 * 60;
export const trainingPackageWorkTagName = 'Training package';

export const externalLinks = {
  salariesRange: 'https://coda.io/d/Company-Guide_dLnC4frK169/Engineering-Progression_su9kJBpu#_lucEjKEG',
  trainingPackageGuide: 'https://coda.io/d/Company-Guide_dLnC4frK169/VirtusLab-Training-Package_suvESDgB#_luL8GK9x',
  addInvoice: 'https://virtuslab4.fakturownia.pl/invoices/new',
  nfsFeedbackSlack: 'https://virtuslab.slack.com/archives/C048BEF4QJF',
  profileCompletion: 'https://virtuslab.slack.com/archives/C053XPW9L80/p1776773598378139',
};

export const profileCompletionGuardStorageKey = 'profileCompletionGuard:dismissed';
export const browserSupportGuardStorageKey = 'browserSupportGuard:dismissed';
export const profileCompletionMinSkills = 5;

export const certificateFileAcceptTypes = '.pdf,.jpg,.jpeg,.png';
export const acceptedLeaveAttachmentTypes = '.pdf'

export const unspecifiedFileContentType = 'application/octet-stream';

export const htmlContentType = 'text/html';

export const pdfContentType = 'application/pdf';

export const nfsUserAppLink = "https://nfs.virtuslab.pro/"

export const benefitPromisor = 'VirtsuLab'

export const trainingPackageCutOffYear = 2023;

export const countriesWithDisabledManualInvoiceUpload: ReadonlyArray<string> = ['PL'];

export enum AbsencesListColumns {
  person = 'person',
}

export enum BookingAvailabilityColumns {
  place = 'place',
}

export const resourceKindIcon: Readonly<Record<ResourceKind, Icon>> = {
  [ResourceKind.DESK]: DesktopIcon,
  [ResourceKind.PARKINGSPOT]: CarIcon,
};
